import { useTranslation } from "react-i18next";
import { useIsMonitor } from "../../../../../hooks/useIsMonitor";
import UnitVehicleAvatar from "../../../../../components/vehicles/UnitVehicleAvatar";
import {
  getTimeAgo,
  type TimeTranslations,
} from "../../../../../utils/getDate";
import type { Unit } from "../../../../../store/admin/unitsStore";
import { CRUDTooltip } from "../../../../../components/base/CRUDTooltip";

interface UnitCardProps {
  unit: Unit;
  themeClasses: {
    cardBg: string;
    cardBorder: string;
    text: string;
    textSecondary: string;
    buttonHover: string;
    buttonText?: string;
    buttonTextHover?: string;
  };
  isDarkMode: boolean;
  getStatusColor: (status: string) => string;
  getStatusText: (status: string) => string;
  onEdit?: (unit: Unit) => void;
  onDetails?: (unit: Unit) => void;
  onTrailerClick?: (unit: Unit) => void;
  onDeviceClick?: (unit: Unit) => void;
  onDelete?: (unit: Unit) => void;
  showActions?: boolean;
  disableCRUD?: boolean;
}

export default function UnitCard({
  unit,
  themeClasses,
  isDarkMode,
  getStatusColor,
  getStatusText,
  onEdit,
  onDetails,
  onTrailerClick,
  onDeviceClick,
  onDelete,
  showActions = true,
  disableCRUD = false,
}: UnitCardProps) {
  const hasTrailer = !!unit.remolque_id || !!unit.remolque;
  const hasDevice = unit.devices && unit.devices.length > 0;
  const { t } = useTranslation("adminSections");
  const { t: tCommon } = useTranslation("common");
  const { isMonitor, isReadOnly } = useIsMonitor();
  const rawType = unit.type as string | string[] | undefined;
  const unitTypeLabel = Array.isArray(rawType)
    ? rawType.map((v) => t(`units.type.${v}`, { defaultValue: v })).join(", ")
    : t(`units.type.${rawType || "other"}`, { defaultValue: rawType || "" });
  const plattform = import.meta.env.VITE_THEME;
  const timeTranslations: TimeTranslations = {
    noData: tCommon("time.noData"),
    rightNow: tCommon("time.rightNow"),
    min: tCommon("time.min"),
    hour: tCommon("time.hour"),
    hours: tCommon("time.hours"),
    day: tCommon("time.day"),
    days: tCommon("time.days"),
    month: tCommon("time.month"),
    months: tCommon("time.months"),
    and: tCommon("time.and"),
  };
  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 hover:shadow-lg transition-all`}
    >
      {/* Unit Header */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center space-x-2">
          <UnitVehicleAvatar
            vehicleId={unit.id}
            name={unit.name}
            pointerModel={unit.pointer_model}
            bodyType={unit.type}
            fallback={
              unit.icon?.includes("http") ? (
            <img
              src={unit.icon}
              alt={unit.name}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div
              className={`rounded-md p-2 px-3.5 ${getStatusColor(
                unit.status?.name || "",
              )}`}
            >
              <i className="ri-truck-line text-lg"></i>
            </div>
          )
            }
          />
          <div>
            <h3
              className={`font-semibold md:max-w-[95px] overflow-hidden text-ellipsis whitespace-nowrap text-sm ${themeClasses.text}`}
              style={{ wordBreak: "keep-all" }}
            >
              {unit.name}
            </h3>
            <p
              className={`text-xs font-semibold ${themeClasses.textSecondary}`}
            >
              {unit.plate}
            </p>
          </div>
        </div>
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
            unit.status?.name || "",
          )}`}
        >
          {getStatusText(unit.status?.name || "")}
        </span>
      </div>

      {/* Unit Info */}
      <div className="space-y-0.5">
        <div className="flex items-center justify-between">
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {t("units.fields.driver")}
          </span>
          <span className={`text-sm ${themeClasses.text}`}>
            {unit.driver?.name || "-"}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {t("units.fields.brand")}
          </span>
          <span className={`text-sm ${themeClasses.text}`}>
            {unit.brand || "-"}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {t("units.fields.model")}
          </span>
          <span className={`text-sm ${themeClasses.text}`}>
            {unit.model || "-"}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {t("units.fields.year")}
          </span>
          <span className={`text-sm ${themeClasses.text}`}>
            {unit.year || "-"}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {t("units.fields.type")}
          </span>
          <span
            className={`text-xs ${
              isDarkMode ? "bg-dark-border" : "bg-gray-100"
            } p-0.5 px-2 rounded-full ${themeClasses.text}`}
          >
            {unitTypeLabel || "-"}
          </span>
        </div>
        {/* <div className="flex items-center justify-between">
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {t("units.fields.fuel")}:
          </span>
          <span className={`text-sm ${themeClasses.text}`}>
            {`(${
              unit.fuelType === "gasoline"
                ? t("units.fields.gasoline")
                : t("units.fields.diesel")
            } ${unit.fuelCapacity || 0}L)`}
          </span>
        </div> */}
        {/* <div className="flex items-center justify-between">
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {t("units.fields.speed")}
          </span>
          <span className={`text-sm ${themeClasses.text}`}>
            {unit?.devices?.length > 0 ? unit.devices[0].mileage : 0} km/h
          </span>
        </div> */}
      </div>

      {/* Truck yard types */}
      <div className="mt-3">
        <p className={`text-[11px] uppercase tracking-wide ${themeClasses.textSecondary} mb-1`}>
          {t("units.fields.truckYardTypes", "Tipos de patio")}
        </p>
        {Array.isArray(unit.truck_yard_types) &&
        unit.truck_yard_types.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {unit.truck_yard_types.map((ty) => (
              <span
                key={ty}
                className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-600 dark:text-cyan-300 border border-cyan-500/30"
              >
                {t(`units.truckYardType.${ty}`, { defaultValue: ty })}
              </span>
            ))}
          </div>
        ) : (
          <span className={`text-xs italic ${themeClasses.textSecondary}`}>
            {t("units.fields.noTruckYardTypes", "Sin tipos de patio")}
          </span>
        )}
      </div>

      {/* Actions */}
      {showActions && (
        <div className="flex space-x-2 mt-2">
          {!isMonitor && !isReadOnly && (
            <CRUDTooltip className="flex-1">
              <button
                className={`w-full py-2 px-3 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} ${themeClasses.buttonTextHover} text-sm font-medium transition-colors disabled:opacity-80 disabled:cursor-not-allowed`}
                onClick={() => onEdit?.(unit)}
                disabled={disableCRUD}
              >
                {t("units.actions.edit")}
              </button>
            </CRUDTooltip>
          )}

          {/* Botón conectar/desconectar Device */}
          {plattform === "central-gps" && (
            <CRUDTooltip>
              <button
                onClick={() => onDeviceClick?.(unit)}
                disabled={disableCRUD}
                className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors relative ${
                  hasDevice
                    ? "bg-blue-500 hover:bg-blue-600 text-white"
                    : `${themeClasses.buttonHover} border ${themeClasses.cardBorder} ${themeClasses.buttonText}`
                }`}
                title={
                  hasDevice
                    ? t("units.device.disconnect")
                    : t("units.device.connect")
                }
              >
                <i className="ri-gps-line"></i>
                {!hasDevice && (
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-primary-500 rounded-full flex items-center justify-center">
                    <i className="ri-add-line text-[8px] text-gray-900"></i>
                  </span>
                )}
              </button>
            </CRUDTooltip>
          )}

          {/* Botón conectar/desconectar Remolque */}
          {plattform === "dumax" && (
            <CRUDTooltip>
              <button
                onClick={() => onTrailerClick?.(unit)}
                disabled={disableCRUD}
                className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors relative ${
                  hasTrailer
                    ? "bg-blue-500 hover:bg-blue-600 text-white"
                    : `${themeClasses.buttonHover} border ${themeClasses.cardBorder} ${themeClasses.buttonText}`
                }`}
                title={
                  hasTrailer
                    ? unit.remolque?.plate
                    : t("units.trailer.connectTrailer")
                }
              >
                <i className="ri-caravan-line"></i>
                {!hasTrailer && (
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-primary-500 rounded-full flex items-center justify-center">
                    <i className="ri-add-line text-[8px] text-gray-900"></i>
                  </span>
                )}
              </button>
            </CRUDTooltip>
          )}
          <button
            onClick={() => onDetails?.(unit)}
            className="py-2 px-3 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg text-sm font-medium transition-colors flex-1"
          >
            {t("units.actions.details")}
          </button>
          {onDelete && !isMonitor && !isReadOnly && (
            <CRUDTooltip>
              <button
                onClick={() => onDelete(unit)}
                disabled={disableCRUD}
                className="py-2 px-3 bg-red-500/10 hover:bg-red-500/20 text-red-500 rounded-lg text-sm transition-colors disabled:opacity-80 disabled:cursor-not-allowed"
                title={t("units.actions.delete", "Eliminar")}
              >
                <i className="ri-delete-bin-line"></i>
              </button>
            </CRUDTooltip>
          )}
        </div>
      )}

      <div className="mt-2 pt-3 border-t border-gray-200 dark:border-gray-700">
        <p className={`text-xs ${themeClasses.textSecondary}`}>
          {t("units.lastUpdate")}:{" "}
          {unit.updatedAt?._seconds
            ? getTimeAgo(unit.updatedAt._seconds, timeTranslations)
            : "-"}
        </p>
      </div>
    </div>
  );
}
